import { type FC, useCallback, useMemo, useState } from 'react';
import { Button, Radio, Spin, Switch } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import type { MetricColumnConfig } from '../../config/tpoGenConfig';
import { isDisplayableMetric } from '../../config/metrics';
import {
    TPO_PROMO_METRICS_LOADING,
    TPO_PROMO_METRICS_LOAD_MORE_BTN,
    TPO_PROMO_METRICS_REFRESH_UPLIFT_BTN,
    TPO_PROMO_METRICS_SECTION,
    TPO_PROMO_METRICS_TOTALS_SWITCH,
    TPO_PROMO_METRICS_VIEW_TAB,
    TPO_PROMO_SPENDS_VIEW_TAB,
} from '../../config/testIds';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { useMetricApplicability } from '../../rules/store/useMetricApplicability';
import type { PPGRow } from '../../types/promoMetrics';
import { useEditSession } from '../EditSession/EditSessionContext';
import { TPOGEN_METRIC_EDITED, TPOGEN_SPEND_EDITED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';
import type { PromoKpiColumns } from './hooks/ui/usePromoKpiColumns';
import KpiSelectDropdown from './components/KpiSelectDropdown';
import ProductSelectDropdown from './components/ProductSelectDropdown';
import PromoMetricsEmptyState from './components/PromoMetricsEmptyState';
import PromoMetricsTable from './components/PromoMetricsTable';

type PromoMetricsView = 'promo-metrics' | 'promo-spends';

interface PromoMetricsSectionProps {
    ppgData: PPGRow[];
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    onAddProducts: (ppgIds: number[]) => void;
    isAddProductsDisabled: boolean;
    isLoadingRecommendedValues: boolean;
    isLoadingNextPage: boolean;
    hasMoreRecommendedValues: boolean | undefined;
    fetchNextRecommendedPage: () => void;
    hasProducts: boolean;
    instanceId: string;
    existingProductIds: number[];
    productOptions: { label: string; value: string }[];
    isLoadingProducts: boolean;
    onRefreshUplift: () => Promise<void>;
    isUpliftPending: boolean;
    isUpliftApplicable: boolean;
    /** False while any visible, enabled, required Promo Meta field is still empty. */
    isPromoMetaComplete: boolean;
    /** Labels of the Promo Meta fields blocking the gate; surfaced in the empty state. */
    missingRequiredLabels: string[];
    /** Server-computed applicable metric keys for this cross-section; `null` => fall back to `metricRules`. */
    applicableMetricKeys: string[] | null;
    /** Server-computed default-visible metric keys; `null` => fall back to `MetricColumnConfig.defaultVisible`. */
    defaultVisibleMetricKeys: string[] | null;
    /** Per-promo-tab KPI column customizations (owned by `PromoTab`; see `usePromoKpiColumns`). */
    kpiColumns: PromoKpiColumns;
}

/**
 * Filters columns to the metrics applicable for the current cross-section state.
 *
 * Applicability is server-computed per request: the recommended-values and
 * get-promo-by-id endpoints return `applicable_metric_keys`, threaded here as
 * `apiApplicableSet`. When present, it is the single source of truth and a column
 * shows only if its key is in the set.
 *
 * `apiApplicableSet === null` means neither endpoint supplied the field (older
 * backend / dev fallback / before any fetch); in that case we fall back to the
 * FE `metricRules` engine (`useMetricApplicability`), where `applicableByKey[key]`
 * is present only for metrics that carry a rule, so a metric without a rule is
 * always applicable (`?? true`).
 */
function getApplicableConfigs(
    configs: MetricColumnConfig[],
    applicableByKey: Record<string, boolean>,
    apiApplicableSet: Set<string> | null,
): MetricColumnConfig[] {
    // `fe_hidden` metrics are never shown or offered in the column picker, even
    // if the group happens to include them; they exist only for fe_formula math.
    const displayable = configs.filter(isDisplayableMetric);
    if (apiApplicableSet) {
        return displayable.filter((c) => apiApplicableSet.has(c.key));
    }
    return displayable.filter((c) => applicableByKey[c.key] ?? true);
}

function sortByOrder(configs: MetricColumnConfig[]): MetricColumnConfig[] {
    return [...configs].sort((a, b) => a.order - b.order);
}

/**
 * Whether a metric is default-visible. Server-computed `default_visible_metric_keys`
 * (threaded as `apiDefaultVisibleSet`) is the source of truth when present; otherwise
 * we fall back to the static per-metric `MetricColumnConfig.defaultVisible` flag.
 */
function isDefaultVisible(config: MetricColumnConfig, apiDefaultVisibleSet: Set<string> | null): boolean {
    return apiDefaultVisibleSet ? apiDefaultVisibleSet.has(config.key) : config.defaultVisible;
}

/**
 * Visible columns are derived from the user's explicit customizations rather than
 * stored directly. A column is visible when it is applicable AND either default-visible
 * or explicitly added by the user, AND it has not been explicitly hidden by the user.
 */
function deriveVisibleConfigs(
    applicableConfigs: MetricColumnConfig[],
    hiddenKeys: Set<string>,
    extraKeys: Set<string>,
    apiDefaultVisibleSet: Set<string> | null,
): MetricColumnConfig[] {
    return applicableConfigs.filter(
        (c) => (isDefaultVisible(c, apiDefaultVisibleSet) || extraKeys.has(c.key)) && !hiddenKeys.has(c.key),
    );
}

const PromoMetricsSection: FC<PromoMetricsSectionProps> = ({
    ppgData,
    onMetricChange,
    onAddProducts,
    isAddProductsDisabled,
    isLoadingRecommendedValues,
    isLoadingNextPage,
    hasMoreRecommendedValues,
    fetchNextRecommendedPage,
    hasProducts,
    instanceId,
    existingProductIds,
    productOptions,
    isLoadingProducts,
    onRefreshUplift,
    isUpliftPending,
    isUpliftApplicable,
    isPromoMetaComplete,
    missingRequiredLabels,
    applicableMetricKeys,
    defaultVisibleMetricKeys,
    kpiColumns,
}) => {
    const tpoGenConfig = useTpoGenConfig();
    // Read-only wins over any other gate (pending/no-products) on the manual refresh trigger.
    const { canEdit } = useEditSession();
    // Rule-engine applicability is kept wired as a fallback for when the backend does not
    // supply `applicable_metric_keys` (see `getApplicableConfigs`).
    const applicableByKey = useMetricApplicability(instanceId);

    // Server-computed keys take precedence; `null` (backend omitted them) => use the FE fallbacks.
    const apiApplicableSet = useMemo(
        () => (applicableMetricKeys ? new Set(applicableMetricKeys) : null),
        [applicableMetricKeys],
    );
    const apiDefaultVisibleSet = useMemo(
        () => (defaultVisibleMetricKeys ? new Set(defaultVisibleMetricKeys) : null),
        [defaultVisibleMetricKeys],
    );
    const [activePromoView, setActivePromoView] = useState<PromoMetricsView>('promo-metrics');
    const [isTreeView, setIsTreeView] = useState(false);
    const [showTotals, setShowTotals] = useState(false);
    // Track only explicit user customizations; visible columns are derived during render
    // so that newly-applicable default-visible columns (e.g. edlp_srp on merchandising)
    // always appear when the promo type changes, while honoring the user's choices.
    // hidden* = default-visible keys the user turned OFF.
    // extra*  = non-default-visible keys the user turned ON.
    // State is owned by `PromoTab` (via `usePromoKpiColumns`) so it survives the
    // first-save spinner remount and is unique per open promo tab.
    const {
        hiddenMetricKeys,
        extraMetricKeys,
        hiddenSpendKeys,
        extraSpendKeys,
        setHiddenMetricKeys,
        setExtraMetricKeys,
        setHiddenSpendKeys,
        setExtraSpendKeys,
    } = kpiColumns;

    const applicableMetricConfigs = useMemo(
        () => sortByOrder(getApplicableConfigs(tpoGenConfig.promoMetricsConfig, applicableByKey, apiApplicableSet)),
        [tpoGenConfig, applicableByKey, apiApplicableSet],
    );

    const applicableSpendConfigs = useMemo(
        () => sortByOrder(getApplicableConfigs(tpoGenConfig.promoSpendsConfig, applicableByKey, apiApplicableSet)),
        [tpoGenConfig, applicableByKey, apiApplicableSet],
    );

    const visibleMetricConfigs = useMemo(
        () => deriveVisibleConfigs(applicableMetricConfigs, hiddenMetricKeys, extraMetricKeys, apiDefaultVisibleSet),
        [applicableMetricConfigs, hiddenMetricKeys, extraMetricKeys, apiDefaultVisibleSet],
    );

    const visibleSpendConfigs = useMemo(
        () => deriveVisibleConfigs(applicableSpendConfigs, hiddenSpendKeys, extraSpendKeys, apiDefaultVisibleSet),
        [applicableSpendConfigs, hiddenSpendKeys, extraSpendKeys, apiDefaultVisibleSet],
    );

    const isMetricsView = activePromoView === 'promo-metrics';
    const applicableConfigs = isMetricsView ? applicableMetricConfigs : applicableSpendConfigs;
    const visibleConfigs = isMetricsView ? visibleMetricConfigs : visibleSpendConfigs;

    const visibleKeys = useMemo(() => visibleConfigs.map((c) => c.key), [visibleConfigs]);

    const kpiSelectOptions = useMemo(
        () => applicableConfigs.map((c) => ({ label: c.title, value: c.key })),
        [applicableConfigs],
    );

    const missingRequiredSubtitle =
        missingRequiredLabels.length > 0 ? `Missing: ${missingRequiredLabels.join(', ')}` : undefined;

    const handleMetricChange = useCallback(
        (ppgId: string, metricKey: string, newValue: number) => {
            if (isMetricsView) {
                trackTpoGenEvent(TPOGEN_METRIC_EDITED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    metric_id: metricKey,
                    edit_type: 'single',
                });
            } else {
                trackTpoGenEvent(TPOGEN_SPEND_EDITED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    spend_bucket_id: metricKey,
                    edit_type: 'single',
                });
            }
            onMetricChange(ppgId, metricKey, newValue);
        },
        [isMetricsView, onMetricChange],
    );

    const handleVisibleKeysChange = useCallback(
        (selected: string[]) => {
            const selectedSet = new Set(selected);
            const setHidden = isMetricsView ? setHiddenMetricKeys : setHiddenSpendKeys;
            const setExtra = isMetricsView ? setExtraMetricKeys : setExtraSpendKeys;

            // Only the currently-applicable keys are offered in the dropdown, so we only
            // reconcile those; customizations for other promo types are left untouched.
            setHidden((prev) => {
                const next = new Set(prev);
                for (const c of applicableConfigs) {
                    if (!isDefaultVisible(c, apiDefaultVisibleSet)) continue;
                    if (selectedSet.has(c.key)) next.delete(c.key);
                    else next.add(c.key);
                }
                return next;
            });
            setExtra((prev) => {
                const next = new Set(prev);
                for (const c of applicableConfigs) {
                    if (isDefaultVisible(c, apiDefaultVisibleSet)) continue;
                    if (selectedSet.has(c.key)) next.add(c.key);
                    else next.delete(c.key);
                }
                return next;
            });
        },
        [applicableConfigs, isMetricsView, apiDefaultVisibleSet],
    );

    return (
        <div data-testid={TPO_PROMO_METRICS_SECTION}>
            <div className="flex flex-row justify-between gap-x-4 mb-4">
                <Radio.Group defaultValue={'promo-metrics'}>
                    <Radio.Button
                        value="promo-metrics"
                        onClick={() => setActivePromoView('promo-metrics')}
                        data-testid={TPO_PROMO_METRICS_VIEW_TAB}
                        data-state-active={isMetricsView ? 'true' : undefined}
                    >
                        <p className={`text-sm ${activePromoView === 'promo-metrics' ? 'font-medium' : ''}`}>
                            {tpoGenConfig.promoMetricsGroupLabel}
                        </p>
                    </Radio.Button>
                    <Radio.Button
                        value="promo-spends"
                        onClick={() => setActivePromoView('promo-spends')}
                        data-testid={TPO_PROMO_SPENDS_VIEW_TAB}
                        data-state-active={!isMetricsView ? 'true' : undefined}
                    >
                        <p className={`text-sm ${activePromoView === 'promo-spends' ? 'font-medium' : ''}`}>
                            {tpoGenConfig.promoSpendsGroupLabel}
                        </p>
                    </Radio.Button>
                </Radio.Group>
                <div className="flex flex-row items-center gap-x-4">
                    {/* <div className="flex flex-row items-center gap-x-1">
                        <p className="text-[#52525C] text-xs">{'Tree View'}</p>
                        <Switch checked={!isTreeView} disabled onChange={(checked) => setIsTreeView(!checked)} />
                        <p className="text-[#52525C] text-xs">{'Flat View'}</p>
                    </div> */}
                    <div className="flex flex-row items-center gap-x-2">
                        <p className="text-[#52525C] text-xs">{'Total'}</p>
                        {/* <Switch checked={showTotals} onChange={setShowTotals} disabled={!hasProducts} /> */}
                        <Switch
                            checked={showTotals}
                            onChange={setShowTotals}
                            disabled={true}
                            data-testid={TPO_PROMO_METRICS_TOTALS_SWITCH}
                            data-state-disabled="true"
                        />
                    </div>
                    {isUpliftApplicable ? (
                        <span
                            className={`flex flex-row items-center gap-x-1 ${
                                isUpliftPending || !hasProducts || !canEdit
                                    ? 'pointer-events-none opacity-50 cursor-not-allowed'
                                    : 'cursor-pointer'
                            }`}
                            onClick={canEdit ? onRefreshUplift : undefined}
                            data-testid={TPO_PROMO_METRICS_REFRESH_UPLIFT_BTN}
                            data-state-disabled={isUpliftPending || !hasProducts || !canEdit ? 'true' : undefined}
                        >
                            <ReloadOutlined spin={isUpliftPending} />
                            <p className="text-[#18181B] text-xs">{tpoGenConfig.labels.promos.refreshUplift}</p>
                        </span>
                    ) : null}
                    <KpiSelectDropdown
                        disabled={isAddProductsDisabled}
                        options={kpiSelectOptions}
                        value={visibleKeys}
                        onChange={handleVisibleKeysChange}
                    />
                    <ProductSelectDropdown
                        disabled={isAddProductsDisabled}
                        onApply={onAddProducts}
                        existingProductIds={existingProductIds}
                        options={productOptions}
                        isLoadingOptions={isLoadingProducts}
                    />
                </div>
            </div>

            {/* Gate first: with required Promo Meta fields missing the recommended-values
                query is disabled, so a spinner here would never resolve into a table. */}
            {!isPromoMetaComplete ? (
                <PromoMetricsEmptyState
                    title="Complete the promo details to view metrics"
                    subtitle={missingRequiredSubtitle}
                />
            ) : isLoadingRecommendedValues ? (
                <PromoMetricsEmptyState title="Loading recommended values..." loading />
            ) : !hasProducts ? (
                <PromoMetricsEmptyState
                    title="Select products to view metrics"
                    subtitle={'Use the "Add Products" button above to get started'}
                />
            ) : (
                <>
                    <PromoMetricsTable
                        metricConfigs={visibleConfigs}
                        isTreeView={isTreeView}
                        ppgData={ppgData}
                        onMetricChange={handleMetricChange}
                        showEditableIndicators={tpoGenConfig.showEditableIndicators}
                        showTotals={showTotals}
                    />
                    {hasMoreRecommendedValues ? (
                        <div className="flex justify-center items-center py-3">
                            {isLoadingNextPage ? (
                                <Spin size="small" data-testid={TPO_PROMO_METRICS_LOADING} />
                            ) : (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={fetchNextRecommendedPage}
                                    data-testid={TPO_PROMO_METRICS_LOAD_MORE_BTN}
                                >
                                    Load more products
                                </Button>
                            )}
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
};

PromoMetricsSection.displayName = 'PromoMetricsSection';

export default PromoMetricsSection;
